import "./AddMoney.css";
import React, { useState, useEffect, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  PaymentRequestButtonElement
} from "@stripe/react-stripe-js";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import { countries as allCountries } from "country-data";
import { Country } from "country-state-city";
import axios from "axios";
import { toast } from "react-toastify";
import { useGetWalletQuery } from "../../API/Transaction";
import { queryClient } from "../../lib/react-query";
import envConfig from "../../environment";
import storage from "@utils/storage";
import { defaultParams } from "@utils/global.constants";
import visa from "../../Assets/Images/Visa.png";
import mastercard from "../../Assets/Images/MasterCard.png";
import amex from "../../Assets/Images/Amex.png";
import discover from "../../Assets/Images/cardShuffleLess.png";
import ellipses from "../../Assets/Images/circle-grp.png";
import wallet from "../../Assets/Images/wallet.png";

const AddMoney = () => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate(); 
  const user = storage.getUser();
  const userId = user?.user?.userId;
  const countries = Country.getAllCountries();
  const [country, setCountry] = useState("");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [params] = useState({ ...defaultParams });
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [onboardingUrl, setOnboardingUrl] = useState<string | null>(null);
  const [finalAmount, setFinalAmount] = useState<number | null>(null);
  const [step, setStep] = useState(1);
  const amountNum = Number(amount) || 0;
  const [platformFee, setPlatformFee] = useState<number>(0);
  const [chargeAmountSmallestUnit, setChargeAmountSmallestUnit] = useState<number | null>(null);
  const [walletAmountSmallestUnit, setWalletAmountSmallestUnit] = useState<number | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  // Fetch wallet amount via RTK Query
  const { data: walletData, refetch: refetchWallet } = useGetWalletQuery(userId, params, {
    enabled: !!userId,
  });
  const walletAmount = walletData?.totalAmount ?? 0;

  useEffect(() => {
    if (!userId) return;
    const fetchSavedCards = async () => {
      try {
        const token = storage.getToken();
        const res = await axios.get(`${envConfig.apiUrl.replace(/\/$/, "")}/stripe/saved-cards`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (Array.isArray(res.data?.data)) {
          setSavedCards(res.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch saved cards:", error);
      }
    };
    fetchSavedCards();
  }, [userId]);

  // Stripe PaymentRequest states for Apple Pay / Google Pay
  const [paymentRequest, setPaymentRequest] = useState<any>(null);
  const [canPayWithWallet, setCanPayWithWallet] = useState(false);

  useEffect(() => {
    if (!stripe || !chargeAmountSmallestUnit || !country || step !== 2) {
      setPaymentRequest(null);
      setCanPayWithWallet(false);
      return;
    }

    const currency = getCurrencyByCountry(country).toLowerCase();

    try {
      console.log("Initializing Stripe PaymentRequest with:", {
        country,
        currency,
        amount: chargeAmountSmallestUnit,
      });

      const pr = stripe.paymentRequest({
        country: country,
        currency: currency,
        total: {
          label: "Lendrivia Wallet Top-up",
          amount: chargeAmountSmallestUnit,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      pr.canMakePayment().then((result) => {
        console.log("Stripe canMakePayment result:", result);
        if (result) {
          setPaymentRequest(pr);
          setCanPayWithWallet(true);
        } else {
          setPaymentRequest(null);
          setCanPayWithWallet(false);
        }
      }).catch((err) => {
        console.error("Error checking canMakePayment:", err);
        setPaymentRequest(null);
        setCanPayWithWallet(false);
      });
    } catch (err) {
      console.error("Failed to create Stripe PaymentRequest:", err);
      setPaymentRequest(null);
      setCanPayWithWallet(false);
    }
  }, [stripe, chargeAmountSmallestUnit, country, step]);

  useEffect(() => {
    if (!paymentRequest) return;

    const handlePaymentMethod = async (ev: any) => {
      const currency = getCurrencyByCountry(country).toLowerCase();
      const token = storage.getToken();

      try {
        // 1. Create PaymentIntent on the backend
        const { data: intentData } = await axios.post(
          `${envConfig.apiUrl.replace(/\/$/, "")}/stripe/create-payment-intent`,
          {
            chargeAmount: chargeAmountSmallestUnit,
            walletAmount: walletAmountSmallestUnit,
            currency,
            userId,
            country,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const clientSecret = intentData.clientSecret || intentData.data?.clientSecret;
        if (!clientSecret) throw new Error("Client secret missing from server response");

        // 2. Confirm the payment intent using Stripe
        const confirmResult = await stripe.confirmCardPayment(
          clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        );

        if (confirmResult.error) {
          ev.complete("fail");
          toast.error(confirmResult.error.message || "Payment confirmation failed");
          return;
        }

        if (confirmResult.paymentIntent?.status === "succeeded") {
          ev.complete("success");
          setIsProcessing(true);

          try {
            await axios.post(
              `${envConfig.apiUrl.replace(/\/$/, "")}/stripe/confirm-wallet-topup`,
              { paymentIntentId: confirmResult.paymentIntent.id, userId },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            await queryClient.invalidateQueries(["wallet", userId]);
            await queryClient.invalidateQueries(["allTransactions", userId]);
            await refetchWallet();
          } catch (confirmErr: any) {
            console.error("Failed to confirm wallet top-up:", confirmErr);
          }

          toast.success("Money added successfully!");
          navigate("/app/wallet");
          setIsProcessing(false);
        } else {
          ev.complete("fail");
          toast.error("Payment was not successful");
        }
      } catch (err: any) {
        ev.complete("fail");
        toast.error(err?.response?.data?.message || err?.message || "Payment failed");
      }
    };

    paymentRequest.on("paymentmethod", handlePaymentMethod);

    return () => {
      paymentRequest.off("paymentmethod", handlePaymentMethod);
    };
  }, [paymentRequest, chargeAmountSmallestUnit, walletAmountSmallestUnit, country, userId]);

  // Helpers
  const getCurrencyByCountry = (countryCode: string): string => {
    const c = allCountries[countryCode];
    return c?.currencies?.[0] || "USD";
  };

  const handleContinue = async () => {
    if (!country) {
      toast.error("Please select a country.");
      return;
    }

    const amtNum = Number(amount);
    if (!amount || isNaN(amtNum) || amtNum <= 0) {
      toast.error("Please enter a valid amount.");
      return;
    }

    try {
      const token = storage.getToken();

      const response = await axios.post(
        `${envConfig.apiUrl.replace(/\/$/, "")}/stripe/calculate-fees`,
        { walletAmount: amtNum },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { grossAmount, stripeFee, walletAmount } = response.data;
      setFinalAmount(grossAmount / 100);
      setPlatformFee(stripeFee / 100);
      setChargeAmountSmallestUnit(grossAmount);
      setWalletAmountSmallestUnit(walletAmount);
      setStep(2);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to calculate fees.");
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      toast.error("Stripe has not loaded yet.");
      return;
    }
    if (!country) {
      toast.error("Please select a country.");
      return;
    }

    if (!chargeAmountSmallestUnit || !walletAmountSmallestUnit) {
      toast.error("Missing calculated amounts. Please try again.");
      return;
    }

    const currency = getCurrencyByCountry(country).toLowerCase();

    setLoading(true);

    try {
      const token = storage.getToken();
      const { data: intentData } = await axios.post(
        `${envConfig.apiUrl.replace(/\/$/, "")}/stripe/create-payment-intent`,
        {
          chargeAmount: chargeAmountSmallestUnit,
          walletAmount: walletAmountSmallestUnit,
          currency,
          userId,
          country,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const clientSecret = intentData.clientSecret || intentData.data?.clientSecret;
      if (!clientSecret) throw new Error("Client secret missing from server response");

      let paymentMethodId = selectedCardId ?? null;

      if (!paymentMethodId) {
        const cardElement = elements.getElement(CardNumberElement);
        if (!cardElement) throw new Error("Card details are missing");

        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: {
            name: `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim(),
          },
        });

        if (error || !paymentMethod) {
          throw new Error(error?.message || "Failed to create payment method");
        }

        paymentMethodId = paymentMethod.id;
      }

      // Confirm the card payment
      const confirmResult = await stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethodId,
        setup_future_usage: "off_session",
      });

      if (confirmResult.error) throw new Error(confirmResult.error.message);
      if (confirmResult.paymentIntent?.status === "succeeded") {
        setIsProcessing(true);
        try {
          await axios.post(
            `${envConfig.apiUrl.replace(/\/$/, "")}/stripe/confirm-wallet-topup`,
            { paymentIntentId: confirmResult.paymentIntent.id, userId },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          await queryClient.invalidateQueries(["wallet", userId]);
          await queryClient.invalidateQueries(["allTransactions", userId]);
          await refetchWallet();
        } catch (confirmErr: any) {
          console.error("Failed to confirm wallet top-up:", confirmErr);
        }
        toast.success("Money added successfully!");
        navigate("/app/wallet");
        setIsProcessing(false);
      } else {
        throw new Error("Payment was not successful");
      }
    } catch (err: any) {
      const errorData = err?.response?.data;

      if (
        errorData?.statusCode === "Stripe account not verified for payouts" &&
        errorData?.data?.onboardingUrl
      ) {
        toast.info("Stripe onboarding required.");
        setOnboardingUrl(errorData.data.onboardingUrl);
        return;
      }

      toast.error(errorData?.message || err?.message || "Payment failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content dashboard">
      <div className="content-wrapper add-money-section">
        <div className="title-search-block d-flex align-items-center justify-content-between">
          <div className="input-btn-fields d-flex align-items-center">
            <button className="back-btn mb-4" onClick={() => navigate(-1)}>
              Back
            </button>
          </div>
        </div>

        <h5 className="heading m-0">Add Money to Wallet</h5>

        <div className="outer-box-wrapper">
          <div className="card-block row">
            {/* Wallet Summary */}
            <div className="col-lg-5 left-card-bg bg-white d-flex align-items-center justify-content-center">
              <div className="lendriva-box credit-card-box position-relative overflow-hidden">
                <div className="inner-box-content h-100 d-flex justify-content-between align-items-end">
                  <div className="content-lower">
                    <p className="m-0">Wallet Holder</p>
                    <h5 className="m-0">
                      {user?.user?.firstName ?? "Andrew"}{" "}
                      {user?.user?.lastName ?? "Lender"}
                    </h5>
                  </div>
                  <div className="wallet-balance">
                    <h2 className="m-0">£{walletAmount}</h2>
                    <p className="m-0">Wallet Balance</p>
                  </div>
                </div>
                <div className="content-upper">
                  <img src={wallet} alt="" className="img-fluid wallet-db position-absolute" />
                </div>
                {/* <div className="grouped-ellipse wallet-grpd-ellipse position-absolute">
                  <img src={ellipses} alt="" className="img-fluid" />
                </div> */}
              </div>
            </div>

            {/* Form */}
            <div className="col-lg-7">
              <div className="form-card">
                <div className="form-title mb-4 d-flex align-items-center">
                  <span className="me-2">
                    <svg width="24" height="24" fill="none">
                      <path
                        d="M0 5C0 4.2 0.3 3.4 0.9 2.9C1.4 2.3 2.2 2 3 2H21C21.8 2 22.6 2.3 23.1 2.9C23.7 3.4 24 4.2 24 5H0ZM0 8V17C0 17.8 0.3 18.6 0.9 19.1C1.4 19.7 2.2 20 3 20H21C21.8 20 22.6 19.7 23.1 19.1C23.7 18.6 24 17.8 24 17V8H0ZM4.5 15.5C4.5 15.1 4.7 14.7 4.9 14.4C5.2 14.2 5.6 14 6 14H7.5C7.9 14 8.3 14.2 8.6 14.4C8.8 14.7 9 15.1 9 15.5C9 15.9 8.8 16.3 8.6 16.6C8.3 16.8 7.9 17 7.5 17H6C5.6 17 5.2 16.8 4.9 16.6C4.7 16.3 4.5 15.9 4.5 15.5Z"
                        fill="#4880ff"
                      />
                    </svg>
                  </span>
                  Enter Card Details
                </div>

                {/* Saved Cards */}
                {savedCards.length > 0 && (
                  <div className="mb-3">
                    <label className="form-label">Saved Cards</label>
                    <div className="d-flex flex-column gap-2">
                      {savedCards.map((card) => (
                        <div key={card.id} className="form-check">
                          <input
                            type="radio"
                            className="form-check-input"
                            name="savedCard"
                            value={card.id}
                            checked={selectedCardId === card.id}
                            onChange={() => setSelectedCardId(card.id)}
                          />
                          <label className="form-check-label">
                            **** **** **** {card.last4} ({card.brand.toUpperCase()}) - Expires {card.expMonth}/{card.expYear}
                          </label>
                        </div>
                      ))}
                      <div className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          name="savedCard"
                          value=""
                          checked={!selectedCardId}
                          onChange={() => setSelectedCardId(null)}
                        />
                        <label className="form-check-label">Use a new card</label>
                      </div>
                    </div>
                  </div>
                )}

                {!selectedCardId && (
                  <>
                    <div className="mb-3">
                      <label className="form-label">Card number</label>
                      <div className="card-number-box position-relative">
                        <div className="form-control stripe-element-wrapper">
                          <CardNumberElement
                            options={{ style: { base: { fontSize: "16px", color: "#32325d" } } }}
                          />
                        </div>
                        <div className="cards-names position-absolute d-flex align-items-center gap-1">
                          {[mastercard, visa, amex, discover].map((img, idx) => (
                            <div key={idx} className="card-box">
                              <img src={img} alt="card" className="img-fluid" />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="row exp-cvc">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Expiration</label>
                        <div className="form-control stripe-element-wrapper">
                          <CardExpiryElement options={{ style: { base: { fontSize: "16px", color: "#32325d" } } }} />
                        </div>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">CVC</label>
                        <div className="form-control stripe-element-wrapper">
                          <CardCvcElement options={{ style: { base: { fontSize: "16px", color: "#32325d" } } }} />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Onboarding Alert */}
                {onboardingUrl && (
                  <div className="alert-box alert-warning mt-4 text-center p-4 m-4">
                    <p>
                      Your Stripe account isn’t ready to receive payouts. Please complete your onboarding.
                    </p>
                    <a
                      href={onboardingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="button-lender w-100 fw-normal mt-4"
                    >
                      Complete Stripe Onboarding
                    </a>
                  </div>
                )}

                {/* STEP 1: Initial Inputs */}
                {step === 1 && (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleContinue();
                    }}
                    className="payment-form onboarding-form"
                  >
                    <div className="mb-3">
                      <label className="form-label">Country</label>
                      <select
                        className="form-select"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        required
                      >
                        <option value="">Select Country</option>
                        {countries.map((c) => (
                          <option key={c.isoCode} value={c.isoCode}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Add Amount</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="12000"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                        required
                      />
                    </div>

                    <button type="submit" className="button-lender w-100 fw-normal">
                      Continue
                    </button>
                  </form>
                )}

                {/* STEP 2: Summary + Add Money */}
                {step === 2 && finalAmount !== null && (
                  <form onSubmit={handleSubmit}>
                    <div className="summary-box p-4 border rounded bg-light mb-4">
                      <div className="form-title mb-4 d-flex align-items-center" style={{ justifyContent: "center" }}>
                        Payment Summary
                      </div>

                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Amount to Add</span>
                        <span className="fw-semibold">£{amountNum.toFixed(2)}</span>
                      </div>

                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Platform Fee</span>
                        <span className="fw-semibold">£{platformFee.toFixed(2)}</span>
                      </div>

                      <hr className="my-3" />

                      <div className="d-flex justify-content-between">
                        <span className="fw-bold">Total to Pay</span>
                        <span className="fw-bold text-success">£{finalAmount?.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Apple Pay / Google Pay Button */}
                    {canPayWithWallet && paymentRequest && (
                      <div className="mb-4">
                        <label className="form-label text-center d-block mb-3 fw-semibold">
                          Fast Pay with Apple Pay / Google Pay
                        </label>
                        <PaymentRequestButtonElement
                          options={{
                            paymentRequest,
                            style: {
                              paymentRequestButton: {
                                type: "default",
                                theme: "dark",
                                height: "48px",
                              },
                            },
                          }}
                        />
                        <div className="text-center my-3 text-muted" style={{ fontSize: "14px", fontWeight: 500 }}>
                          <span>— OR PAY WITH CREDIT CARD —</span>
                        </div>
                      </div>
                    )}

                    <button
                      type="submit"
                      className="button-lender w-100 fw-normal d-flex justify-content-center align-items-center"
                      disabled={loading || isProcessing}
                    >
                      {isProcessing ? (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      ) : null}
                      {isProcessing ? "Processing..." : "Add Money"}
                    </button>

                    <button
                      type="button"
                      className="btn w-100 mt-3"
                      onClick={() => setStep(1)}
                    >
                      Go Back
                    </button>
                  </form>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddMoney;
